import "../index.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useState, useEffect, useCallback, useRef } from "react";
import ApiCall from "../auth/ApiCall";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import MyChart from "../components/MyChart";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AccessVerification, RuleLimitCheck } from "../auth/UserProfile";
//import { Game } from "../components/Game";

interface Game {
  id: number;
  sport: string;
  sport_id: number;
  home_team: string;
  away_team: string;
  home_team_id: number;
  away_team_id: number;
  start_time: string | number; // ISO 8601 string or Unix timestamp
  home_score: number;
  away_score: number;
  score_details: Record<string, Record<string, unknown>>;
  game_status: Record<string, unknown>;
  odds: Record<string, Record<string, unknown>>;
  watching: boolean;
}

export default function GameDetails() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentGameId, setCurrentGameId] = useState<number>(
    gameId ? parseInt(gameId, 10) : -1,
  );
  const [gameData, setGameData] = useState<Record<string, unknown>>();
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [gamesError, setGamesError] = useState<string | null>(null);
  const [gameDataError, setGameDataError] = useState(null);
  const [lastChartRefreshed, setLastChartRefreshed] = useState<Date | null>(
    null,
  );
  // const [refreshing, setRefreshing] = useState(false);
  // const [isGamesLoading, setIsGamesLoading] = useState(true);
  //  const [sportsMetadata, setSportsMetadata] = useState<Record<string, unknown>>({});
  const [games, setGames] = useState<Game[]>([]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [hasNoGames, setHasNoGames] = useState(false);
  //   const [showAwayTeam, setShowAwayTeam] = useState(false);
  const CHART_PARAM_MAP: Record<string, string> = {
    line: "OverUnder",
    moneyline: "MoneylineHome",
    spread: "SpreadAway",
    MoneylineHome: "MoneylineHome",
    MoneylineDraw: "MoneylineDraw",
    MoneylineAway: "MoneylineAway",
  };
  const [chartType, setChartType] = useState(
    CHART_PARAM_MAP[searchParams.get("chart") ?? ""] ?? "OverUnder",
  );
  const initialGameSetRef = useRef(!!gameId);
  const currentGameRef = useRef<Game | null>(null);
  useEffect(() => {
    currentGameRef.current = currentGame;
  }, [currentGame]);
  // const chartAbortRef = useRef<AbortController | null>(null);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset chart type to a valid option when switching between soccer and non-soccer
  useEffect(() => {
    const soccerSport =
      currentGame?.sport_id === 7 || currentGame?.sport_id === 8;
    const soccerTypes = ["MoneylineHome", "MoneylineDraw", "MoneylineAway"];
    if (soccerSport && !soccerTypes.includes(chartType)) {
      setChartType("MoneylineHome");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.sport_id]);

  // Verify user has an active subscription
  useEffect(() => {
    const redirectPath = AccessVerification() || RuleLimitCheck();
    if (redirectPath) {
      navigate(redirectPath);
    }
  }, [navigate]);

  // Sync state when URL gameId param changes
  const prevGameIdRef = useRef<string | undefined>(gameId);
  useEffect(() => {
    if (gameId && gameId !== prevGameIdRef.current) {
      prevGameIdRef.current = gameId;
      const newGameId = parseInt(gameId, 10);
      // Clear chart immediately before setting new ID
      setGameData(undefined);
      setCurrentGameId(newGameId);
      // Find the game in our list if available
      const game = games.find((g) => g.id === newGameId);
      if (game) {
        setCurrentGame(game);
      }
    }
  }, [gameId, games]);

  const fetchChartData = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        "/api/game_data/" + currentGameId + "/",
        { signal },
      );
      if (signal.aborted) return;

      if ("error" in response) {
        throw new Error("Network response was not ok");
      } else {
        const data = response as Record<string, unknown>;
        setGameData(data);
        setLastChartRefreshed(new Date());
        setGameDataError(null);

        // If currentGame is not set (e.g. completed game not in active list),
        // build it from the chart data response
        if (!currentGameRef.current && data["game_data"]) {
          const gd = data["game_data"] as Record<string, unknown>;
          setCurrentGame({
            id: currentGameId,
            sport: (gd["sport"] as string) || "",
            sport_id: (gd["sport_id"] as number) || 0,
            home_team: (gd["home_team"] as string) || "",
            away_team: (gd["away_team"] as string) || "",
            home_team_id: (gd["home_team_id"] as number) || 0,
            away_team_id: (gd["away_team_id"] as number) || 0,
            start_time: (gd["start_time"] as string | number) || "",
            home_score: (gd["home_score"] as number) || 0,
            away_score: (gd["away_score"] as number) || 0,
            score_details:
              (gd["score_details"] as Record<
                string,
                Record<string, unknown>
              >) || {},
            game_status: (gd["game_status"] as Record<string, unknown>) || {},
            odds: (gd["odds"] as Record<string, Record<string, unknown>>) || {},
            watching: false,
          });
        }
      }
      setLastChartRefreshed(new Date());
      setGamesError(null);
    } catch (err: unknown) {
      if (signal.aborted) return;
      if (lastChartRefreshed === null) {
        setGamesError(
          "Failed to fetch data: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      }
      // console.error("Fetch error:", err);
      // } finally {
    }
  }, [currentGameId]);

  // Initial load + auto-refresh (5s, backs off to 15s on a slow connection)
  useAdaptivePolling(fetchChartData);

  const fetchData = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        "/api/active_games_data/",
        { signal },
      );
      if (signal.aborted) return;
      if ("error" in response) {
        throw new Error("Network response was not ok");
      } else {
        const data = response as Record<string, unknown>;
        const allGames = data["events"] as Game[];
        const gamesData = allGames;
        setGames(gamesData);
        if (gamesData.length === 0 && !gameId) {
          setHasNoGames(true);
        } else if (!initialGameSetRef.current) {
          initialGameSetRef.current = true;
          // If gameId is in URL, use that; otherwise use first game
          if (gameId) {
            const urlGameId = parseInt(gameId, 10);
            const game = gamesData.find((g) => g.id === urlGameId);
            if (game) {
              setCurrentGame(game);
              setCurrentGameId(urlGameId);
            } else if (gamesData[0]) {
              setCurrentGame(gamesData[0]);
              setCurrentGameId(gamesData[0].id);
            }
          } else if (gamesData[0]) {
            setCurrentGame(gamesData[0]);
            setCurrentGameId(gamesData[0].id);
          }
        } else if (currentGameId > 0 && !currentGame) {
          // currentGame not set yet but we have an ID - find it in the list
          const game = gamesData.find((g) => g.id === currentGameId);
          if (game) {
            setCurrentGame(game);
          }
        }
        //        setSportsMetadata(data["sportsMetadata"] as Record<string, unknown>);
      }
      setLastRefreshed(new Date());
      setGamesError(null);
    } catch (err: unknown) {
      if (signal.aborted) return;
      if (lastRefreshed === null) {
        setGamesError(
          "Failed to fetch data: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      }
      // console.error("Fetch error:", err);
    } finally {
      // setIsGamesLoading(false);
      //      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  useAdaptivePolling(fetchData);

  const toggleGamesList = (): void => {
    setMenuVisible(!menuVisible);
  };

  const closeMenu = (): void => {
    setMenuVisible(false);
  };

  const navigateToGame = (gameIndex: number): void => {
    setMenuVisible(false);
    if (games[gameIndex] && games[gameIndex].id !== currentGameId) {
      // Navigate to the new game URL for a clean page load
      navigate(`/game/${games[gameIndex].id}`);
    }
  };

  const getIconAndColor = (sport: number): string => {
    const sportIcons: Record<number, string> = {
      1: "🏀 ", // NBA
      2: "🏀 ", // NCAAB
      3: "🏒 ", // NHL
      4: "⚾️ ", // MLB
      5: "🏈 ", // NFL
      6: "🏈 ", // NCAAF
      7: "⚽️ ", // MLS
      8: "⚽️ ", // World Cup
    };
    return sportIcons[sport] || "";
  };

  const negateSpreadData = (
    data: Array<Record<string, string | number | null | undefined>>,
  ): Array<Record<string, string | number | null | undefined>> => {
    const result = data.map((point) => {
      const negatedPoint: Record<string, string | number | null | undefined> =
        {};
      for (const key in point) {
        const value = point[key];
        // Don't negate timestamp field, only negate the odds values
        if (typeof value === "number" && key !== "timestamp") {
          negatedPoint[key] = -value;
        } else {
          negatedPoint[key] = value;
        }
      }
      return negatedPoint;
    });
    return result;
  };

  if (hasNoGames) {
    return (
      <div>
        <PageNav />
        <div className="show-desktop">
          <div className="main_div">
            <div className="center_me" style={{ padding: 40 }}>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                No rules defined yet. Go to Create Rule to make your first one.
              </p>
            </div>
          </div>
        </div>
        <div className="show-mobile">
          <div className="main_div">
            <div className="center_me row" style={{ padding: 40 }}>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                No rules defined yet.
              </p>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                Go to Create Rule to make your first one.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (currentGameId < 0 || !currentGame) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <p className="font_family_base">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (gamesError || gameDataError) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <span>⛔️</span> {gamesError}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const gameNotStarted = currentGame
    ? (() => {
        const raw = currentGame.start_time;
        let ms: number;
        if (typeof raw === "number") {
          ms = raw < 1e10 ? raw * 1000 : raw;
        } else {
          const asNum = Number(raw);
          ms = !isNaN(asNum)
            ? asNum < 1e10
              ? asNum * 1000
              : asNum
            : Date.parse(String(raw));
        }
        return ms > Date.now();
      })()
    : false;
  const oddsKey = gameNotStarted ? "pregame_odds" : "live_odds";
  const isSoccer = currentGame?.sport_id === 7 || currentGame?.sport_id === 8;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            paddingRight: "10px",
            paddingLeft: "10px",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Header Row */}
          <div
            className="center_me"
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            {/* <button
              onClick={toggleGamesList}
              className="font_family_base background_1 border_radius"
              style={{
                // backgroundColor: "#181818",
                // borderRadius: 8,
                padding: "10px 10px",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                width: "80px",

                flex: "0 0 auto",
              }}
            >
              &lt; Back
            </button>
 */}
            <button
              onClick={toggleGamesList}
              className="font_family_base "
              style={{
                padding: "15px 20px",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                flex: "0 0 auto",
                background: "transparent",
              }}
            >
              <span
                className="font_family_base"
                style={{
                  fontSize: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {getIconAndColor(currentGame.sport_id)}{" "}
                {isSoccer
                  ? `${currentGame.home_team} vs ${currentGame.away_team}`
                  : `${currentGame.away_team} vs ${currentGame.home_team}`}{" "}
                ▼
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: "bold",
                    padding: "2px 6px",
                    borderRadius: 4,
                    backgroundColor: gameNotStarted ? "#555" : "#c0392b",
                    color: "#fff",
                  }}
                >
                  {gameNotStarted ? "Pregame" : "Live"}
                </span>
              </span>
            </button>
            {/* <span className="font_family_base">
                {getIconAndColor(currentGame.sport_id)} {currentGame.away_team}{" "}
                vs {currentGame.home_team} ▼
              </span> */}
            {/* <div
              className="center_me"
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 20,
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={toggleGamesList}
                className="font_family_base "
                style={{
                  padding: "15px 20px",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  flex: "0 0 auto",
                  background: "transparent",
                }}
              >
                <span className="font_family_base" style={{ fontSize: 16 }}>
                  XXX
                </span>
              </button>
            </div> */}
          </div>
        </div>
        <div className="center_me" style={{ padding: "8px 0" }}>
          <p
            className="font_family_base"
            style={{ fontSize: 16, color: "#4CAF50" }}
          >
            {gameNotStarted
              ? (() => {
                  const raw = currentGame.start_time;
                  let ms: number;
                  if (typeof raw === "number") {
                    ms = raw < 1e10 ? raw * 1000 : raw;
                  } else {
                    const asNum = Number(raw);
                    ms = !isNaN(asNum)
                      ? asNum < 1e10
                        ? asNum * 1000
                        : asNum
                      : Date.parse(String(raw));
                  }
                  return new Date(ms).toLocaleString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  });
                })()
              : isSoccer
                ? `${currentGame.home_team}: ${currentGame.home_score} - ${currentGame.away_team}: ${currentGame.away_score}`
                : `${currentGame.away_team}: ${currentGame.away_score} - ${currentGame.home_team}: ${currentGame.home_score}`}
          </p>
        </div>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            paddingRight: "10px",
            paddingLeft: "10px",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Header Row */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            {/* <button
              onClick={toggleGamesList}
              className="font_family_base background_1 border_radius"
              style={{
                // backgroundColor: "#181818",
                // borderRadius: 8,
                padding: "10px 10px",
                color: "#fff",
                width: "80px",

                border: "none",
                cursor: "pointer",
                flex: "0 0 auto",
              }}
            >
              Games {menuVisible ? "▲" : "▼"}
            </button> */}

            <div
              className="font_family_base center_me  border_radius"
              style={{
                padding: "10px 10px",
                color: "#fff",
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  className="font_family_base"
                  style={{
                    width: "auto",
                    padding: "10px",
                    fontSize: 16,
                    fontWeight: "bold",
                    backgroundColor: "##FFF",
                    color: "#000",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  {isSoccer ? (
                    <>
                      <option value="MoneylineHome">Moneyline (Home)</option>
                      <option value="MoneylineDraw">Moneyline (Draw)</option>
                      <option value="MoneylineAway">Moneyline (Away)</option>
                    </>
                  ) : (
                    <>
                      <option value="OverUnder">Over/Under</option>
                      <option value="OverPrice">Over Price</option>
                      <option value="UnderPrice">Under Price</option>
                      <option value="SpreadAway">Spread (Away)</option>
                      <option value="SpreadHome">Spread (Home)</option>
                      <option value="SpreadAwayPrice">Spread (Away) Price</option>
                      <option value="SpreadHomePrice">Spread (Home) Price</option>
                      <option value="MoneylineAway">Moneyline (Away)</option>
                      <option value="MoneylineHome">Moneyline (Home)</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>
        {!gameData ? (
          <div className="center_me" style={{ padding: 40 }}>
            <p className="font_family_base">Loading chart...</p>
          </div>
        ) : (
          <>
            {chartType === "OverUnder" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "over_under"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
                showPlusSign={false}
              />
            )}
            {chartType === "OverPrice" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "over_price"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "UnderPrice" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "under_price"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "SpreadAway" && (
              <MyChart
                data={negateSpreadData(
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "spread"
                  ] as Array<
                    Record<string, string | number | null | undefined>
                  >,
                )}
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "SpreadHome" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "spread"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "SpreadAwayPrice" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "spread_away_price"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "SpreadHomePrice" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "spread_home_price"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "MoneylineAway" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "away_outcome"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "MoneylineDraw" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "draw_outcome"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
            {chartType === "MoneylineHome" && (
              <MyChart
                data={
                  (gameData[oddsKey] as Record<string, unknown>)[
                    "home_outcome"
                  ] as Array<Record<string, string | number | null | undefined>>
                }
                books={gameData["books"] as string[]}
                bookColors={gameData["bookmakers"] as Record<string, string>}
                isLiveGame={!gameNotStarted}
                startTime={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "start_time"
                  ] as number
                }
                isMoneyline={false}
                vertical_markers={
                  (gameData["game_data"] as Record<string, unknown>)[
                    "vertical_markers"
                  ] as Record<string, number>
                }
                game_part_name={gameData["sport_part_name"] as string}
              />
            )}
          </>
        )}
        {/* Side Menu Overlay */}
        {menuVisible && (
          <>
            <div
              onClick={closeMenu}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: "min(80%, 300px)",
                backgroundColor: "#000",
                borderRight: "1px solid #333",
                zIndex: 1000,
                overflowY: "auto",
                padding: "20px",
              }}
            >
              <h2
                className="font_family_base"
                style={{
                  color: "#4CAF50",
                  marginBottom: 20,
                  textAlign: "center",
                }}
              >
                GAMES
              </h2>
              {games.map((game, index) => {
                const raw = game.start_time;
                let startMs: number;
                if (typeof raw === "number") {
                  startMs = raw < 1e10 ? raw * 1000 : raw;
                } else {
                  const asNum = Number(raw);
                  startMs = !isNaN(asNum)
                    ? asNum < 1e10
                      ? asNum * 1000
                      : asNum
                    : Date.parse(String(raw));
                }
                const isPregame = startMs > Date.now();
                return (
                  <button
                    key={game.id}
                    onClick={() => navigateToGame(index)}
                    className="font_family_base"
                    style={{
                      width: "100%",
                      padding: "15px",
                      marginBottom: "10px",
                      backgroundColor:
                        currentGameId === game.id ? "#007bff" : "#181818",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {getIconAndColor(game.sport_id)} {game.away_team} vs{" "}
                        {game.home_team}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: "bold",
                          padding: "2px 6px",
                          borderRadius: 4,
                          backgroundColor: isPregame ? "#555" : "#c0392b",
                          color: "#fff",
                          marginLeft: 8,
                          flexShrink: 0,
                        }}
                      >
                        {isPregame ? "Pregame" : "Live"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
